import { StatIcon, type StatName } from '@/components/game/GameIcons'
import { HintBubble } from '@/components/game/ui'

export interface CombatStatValues {
  manoeuvre: number
  bordee: number
  abordage: number
  armure: number
  hp: number
  cargo: number
  crew: number
}

/**
 * Single source of truth for what each combat capability means, in plain naval
 * terms — no cryptic "M3/B2" codes. Shared by the fleet readouts and the
 * island Fort, so the same hover tooltip explains a stat wherever it appears.
 */
export const STAT_INFO: Record<StatName, { label: string; hint: string }> = {
  manoeuvre: { label: 'Manœuvre', hint: 'Prend l\'avantage du vent. Qui manœuvre le mieux tire en premier à la bordée.' },
  bordee: { label: 'Bordée', hint: 'Canons longue portée. Plus de bordée = plus de coups au but à distance.' },
  abordage: { label: 'Abordage', hint: 'Combat au corps à corps quand les coques s\'accrochent.' },
  armure: { label: 'Armure', hint: 'Blindage : l\'attaquant doit l\'égaler pour percer la coque, sinon les boulets ricochent.' },
  hp: { label: 'Coque', hint: 'Points de coque : nombre de coups encaissés avant de couler.' },
  cargo: { label: 'Cale', hint: 'Capacité de cargaison pour le commerce.' },
  crew: { label: 'Équipage', hint: 'Nombre de marins à bord. C\'est lui qui fixe les rations de rhum d\'une mission : équipage × heures de mer. Un galion embarque plus de monde qu\'un légendaire, et coûte donc plus cher à nourrir.' },
}

/** Tooltip content for a stat: bold label + the explanation. */
export function statTip(name: StatName) {
  const { label, hint } = STAT_INFO[name]
  return (
    <>
      <span className="font-semibold text-sand-200">{label}</span> ({hint})
    </>
  )
}

const STAT_ORDER: StatName[] = ['manoeuvre', 'bordee', 'abordage', 'armure', 'hp', 'cargo', 'crew']

/** The three stats a combat module can lift — exactly the {@link BattlePhase} names. */
const BOOSTABLE: ReadonlySet<string> = new Set<StatName>(['manoeuvre', 'bordee', 'abordage'])

/**
 * Stat names lifted by the ship's equipped COMBAT modules. Each combat module
 * grants +1 die in its phase (Quille→manoeuvre, Canons→bordee, Grappins→abordage)
 * and the module's `phase` string IS the stat name. Utility modules carry no
 * phase (→ null), and armure/hp are never boosted. Cargo is a utility boost,
 * handled by {@link boostedShipStats}. Drives the green readout in the stat rows.
 */
export function boostedStats(modules: ReadonlyArray<{ phase: string | null }>): Set<StatName> {
  const set = new Set<StatName>()
  for (const m of modules) {
    if (m.phase && BOOSTABLE.has(m.phase)) set.add(m.phase as StatName)
  }
  return set
}

/**
 * The full boosted set for a ship: its combat-module phases (see {@link boostedStats})
 * plus `cargo` when a hold module has lifted the effective hold past the base
 * `cs` — so the Cale renforcée reads green just like Canons longs lifts bordée —
 * plus `hp` when the owner's permanent Forge Charpente tech has thickened the
 * hull (the API's `combatStats.hp` is already the effective value).
 */
export function boostedShipStats(ship: {
  modules: ReadonlyArray<{ phase: string | null }>
  cargoCapacity: number
  cs: number
  forgeBonusHp: number
}): Set<StatName> {
  const set = boostedStats(ship.modules)
  if (ship.cargoCapacity > ship.cs) set.add('cargo')
  if (ship.forgeBonusHp > 0) set.add('hp')
  return set
}

/**
 * Discreet native tooltips explaining WHY a stat reads green when the boost
 * comes from the Forge (a module boost is already self-explanatory via the
 * ship's module list). Today only the hull: « +N Forge (Charpente) ».
 */
export function forgeBoostTitles(ship: { forgeBonusHp: number }): Partial<Record<StatName, string>> {
  return ship.forgeBonusHp > 0 ? { hp: `+${ship.forgeBonusHp} Forge (Charpente)` } : {}
}

/**
 * Colonnes de la grille de stats. DOIT rester synchronisé avec le `grid-cols-4`
 * littéral de {@link ShipCombatStatsRow} : le JIT Tailwind ne voit pas une classe
 * construite dynamiquement, donc la classe reste écrite en dur et cette constante
 * sert uniquement à l'ancrage des bulles d'aide.
 */
const STAT_COLS = 4

/** Edge-anchor the first/last icon of a row so its tooltip can't spill off the
 *  viewport on mobile (first opens rightward, last leftward). */
function edgeAlign(i: number, len: number): 'start' | 'end' | 'center' {
  if (i === 0) return 'start'
  if (i === len - 1) return 'end'
  return 'center'
}

export function ShipCombatStatsRow({
  stats,
  boosted,
  boostTitles,
}: {
  stats: CombatStatValues
  /** Stat names lifted by equipped modules — rendered green (see {@link boostedStats}). */
  boosted?: ReadonlySet<StatName>
  /** Optional per-stat provenance note (see {@link forgeBoostTitles}) — shown as a native title on the value. */
  boostTitles?: Partial<Record<StatName, string>>
}) {
  return (
    // 4 colonnes (donc 2 rangées pour 7 stats) et non 7 d'un rang : la carte qui
    // porte ce bloc est ÉTROITE aux deux extrémités — ~301 px en mobile, ~380 px
    // en desktop où le conteneur passe en `md:grid-cols-2`, et large seulement
    // entre les deux. Les libellés sont des mots insécables ("Manœuvre" réclame
    // 46 px) : à 7 colonnes il faudrait descendre sous 7 px de police pour tenir
    // en mobile. À 4 colonnes chaque libellé dispose de ~59 px au pire, sans
    // rogner ni la police ni le padding.
    <div className="grid grid-cols-4 gap-1.5 text-center">
      {STAT_ORDER.map((name, i) => (
        <div key={name} className="group/tip relative bg-ocean-900/45 rounded p-1.5">
          <StatIcon name={name} className="w-4 h-4 mx-auto text-sand-300/90" />
          <p
            className={`text-base font-display tabular-nums leading-tight mt-0.5 ${
              boosted?.has(name) ? 'text-green-300 font-semibold' : 'text-sand-200'
            }`}
            title={boostTitles?.[name]}
          >
            {stats[name]}
          </p>
          <p className="text-[9px] text-ocean-400 leading-tight">{STAT_INFO[name].label}</p>
          {/* Ancrage sur la POSITION EN COLONNE, pas sur l'index global : la grille
              fait 2 rangées, donc l'index 3 est en bord droit et l'index 4 en bord
              gauche. Raisonner sur l'index global ferait déborder leurs bulles. */}
          <HintBubble align={edgeAlign(i % STAT_COLS, STAT_COLS)} label={statTip(name)} />
        </div>
      ))}
    </div>
  )
}

/**
 * Condensed single-line readout (icon + value) for the fleet's compact "list"
 * view — same metadata + hover tooltip, no per-stat boxes/labels.
 */
export function ShipCombatStatsInline({
  stats,
  boosted,
  boostTitles,
}: {
  stats: CombatStatValues
  /** Stat names lifted by equipped modules — rendered green (see {@link boostedStats}). */
  boosted?: ReadonlySet<StatName>
  /** Optional per-stat provenance note (see {@link forgeBoostTitles}) — shown as a native title on the value. */
  boostTitles?: Partial<Record<StatName, string>>
}) {
  return (
    <div className="flex items-center gap-2 sm:gap-2.5">
      {STAT_ORDER.map((name, i) => (
        <span key={name} className="group/tip relative flex items-center gap-1 text-sm text-ocean-100">
          <StatIcon name={name} className="w-3.5 h-3.5 text-sand-300/80" />
          <span
            className={`tabular-nums ${boosted?.has(name) ? 'text-green-300 font-semibold' : ''}`}
            title={boostTitles?.[name]}
          >
            {stats[name]}
          </span>
          <HintBubble align={edgeAlign(i, STAT_ORDER.length)} label={statTip(name)} />
        </span>
      ))}
    </div>
  )
}
